import { useState, useEffect } from 'react';
import { useConventionReportParticipants } from '@/services/useConventionReport';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

const formatRp = (n: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n);

interface ParticipantTableProps {
  periodId: number;
  defaultPackageId?: string;
  defaultSearch?: string;
  packages?: any[];
}

export function ParticipantTable({ periodId, defaultPackageId, defaultSearch, packages = [] }: ParticipantTableProps) {
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState(defaultSearch || '');
  const [paymentStatus, setPaymentStatus] = useState<string>('all');
  const [packageId, setPackageId] = useState<string>(defaultPackageId || 'all');

  useEffect(() => {
    if (defaultPackageId !== undefined) {
      setPackageId(defaultPackageId);
      setPage(1);
    }
  }, [defaultPackageId]);

  useEffect(() => {
    if (defaultSearch !== undefined) {
      setSearch(defaultSearch);
      setPage(1);
    }
  }, [defaultSearch]);
  
  const { data, isLoading } = useConventionReportParticipants(periodId, {
    page: showAll ? 1 : page,
    per_page: showAll ? 9999 : 10,
    search,
    payment_status: paymentStatus === 'all' ? undefined : paymentStatus,
    package_id: packageId === 'all' ? undefined : packageId,
  });

  return (
    <div className="space-y-4 mt-6 bg-white dark:bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-center mb-2">
        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <Input 
            placeholder="Cari nama atau institusi..." 
            value={search} 
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }} 
            className="w-full sm:w-[250px]"
          />
          <Select value={packageId} onValueChange={(val) => { setPackageId(val); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder="Semua Paket" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Paket / Rute</SelectItem>
              {packages.map((pkg) => (
                <SelectItem key={pkg.package_id} value={pkg.package_id.toString()}>
                  {pkg.package_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-auto">
          <Select value={paymentStatus} onValueChange={(val) => { setPaymentStatus(val); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Status Pembayaran" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="paid">Lunas</SelectItem>
              <SelectItem value="pending">Menunggu</SelectItem>
              <SelectItem value="unpaid">Belum Bayar</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border border-slate-100 dark:border-slate-800 overflow-hidden shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama Peserta</TableHead>
              <TableHead>Institusi</TableHead>
              <TableHead>Paket</TableHead>
              <TableHead>Status Pembayaran</TableHead>
              <TableHead>Nominal</TableHead>
              <TableHead>RFID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">Memuat data...</TableCell>
              </TableRow>
            ) : data?.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">Tidak ada data peserta</TableCell>
              </TableRow>
            ) : (
              data?.data.map((reg) => (
                <TableRow key={reg.registration_id}>
                  <TableCell className="font-medium">{reg.participant?.full_name}</TableCell>
                  <TableCell>{reg.participant?.institution}</TableCell>
                  <TableCell>{reg.package?.name}</TableCell>
                  <TableCell>
                    <Badge variant={
                      reg.payment_status === 'paid' ? 'default' : 
                      reg.payment_status === 'pending' ? 'secondary' : 'destructive'
                    }>
                      {reg.payment_status === 'paid' ? 'Lunas' : 
                       reg.payment_status === 'pending' ? 'Menunggu Konfirmasi' : 'Belum Bayar'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {formatRp(reg.latest_payment?.amount || reg.package?.price || 0)}
                  </TableCell>
                  <TableCell>
                    {reg.rfid_number ? <Badge variant="outline" className="bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800">Terscan</Badge> : <span className="text-slate-400">-</span>}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>Total: {data?.meta.total || 0} data</span>
          {!showAll && data?.meta.last_page && data.meta.last_page > 1 && (
            <span>• Halaman {data.meta.current_page} dari {data.meta.last_page}</span>
          )}
          {data?.meta.total && data.meta.total > 10 && (
            <Button variant="link" size="sm" onClick={() => { setShowAll(!showAll); setPage(1); }} className="h-auto p-0 ml-1 text-primary hover:text-primary/80 font-medium">
              {showAll ? "(Tampilkan Lebih Sedikit)" : "(Tampilkan Semua)"}
            </Button>
          )}
        </div>
        {!showAll && (
          <div className="space-x-2">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Sebelumnya
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === data?.meta.last_page || !data?.meta.last_page}
              onClick={() => setPage(p => p + 1)}
            >
              Selanjutnya
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
